import logger from '../../utils/logger';
import React, { useState, useEffect, useRef } from 'react';
import { LOCATION_STAGES, getLocationStageColor, ongoingSecondsForStatus } from '../../utils/utils';
import '../../style/LocationModal.css';

function formatElapsed(totalSeconds) {
  totalSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Takes an age in seconds, not a timestamp: the server computes it with
 * TIMESTAMPDIFF so no timezone offset can leak into it.
 */
function formatSavedAt(ageSeconds) {
  const seconds = Number(ageSeconds);
  if (!Number.isFinite(seconds)) return 'earlier';
  const minutes = Math.floor(Math.max(seconds, 0) / 60);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Patient-location stepper + per-stage time tracking, opened from CheckIn.js's
 * "Location" button and from PatientAbout.js's location badge. Also surfaces
 * an unfinished registration draft for the office assistant to resume or
 * discard — a named-patient, deliberate click, never an automatic prompt,
 * since the kiosk this may run on is a shared device.
 *
 * Props:
 *  appointment      {schedule_id, patient_name, ris_patients_id}
 *  currentStatus    current location string (see LOCATION_STAGES)
 *  onSelectStatus   (status) => void
 *  onCancel         () => void
 *  onResumeRegistration  (appointment) => void   optional
 */
function LocationModal({ appointment, currentStatus, onSelectStatus, onCancel, onResumeRegistration }) {
  const [statusDurations, setStatusDurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());
  const [draft, setDraft] = useState(null);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const ongoingBaselineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatusTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/scheduling/statusTimes?schedule_id=${appointment.schedule_id}`);
        const data = await response.json();
        if (!cancelled) {
          const durations = Array.isArray(data) ? data : [];
          setStatusDurations(durations);
          const ongoingStatus = durations.find((d) => d.ongoing)?.status || null;
          const ongoingSeconds = ongoingStatus ? ongoingSecondsForStatus(durations, ongoingStatus) : null;
          ongoingBaselineRef.current = ongoingStatus
            ? { status: ongoingStatus, baselineMs: Date.now() - ongoingSeconds * 1000 }
            : null;
        }
      } catch (error) {
        logger.error('Error fetching status times', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (appointment?.schedule_id) fetchStatusTimes();
    return () => { cancelled = true; };
  }, [BASE_URL, appointment?.schedule_id]);

  useEffect(() => {
    const tickId = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchDraft = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/patientRegistrationReferred/draft?ris_patients_id=${appointment.ris_patients_id}&metaOnly=true`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setDraft(data.draft || null);
      } catch (error) {
        logger.error('Error fetching registration draft', error);
      }
    };
    if (appointment?.ris_patients_id) fetchDraft();
    return () => { cancelled = true; };
  }, [BASE_URL, appointment?.ris_patients_id]);

  const handleDiscardDraft = async () => {
    if (!window.confirm('Discard the saved registration answers for this patient? This cannot be undone.')) return;
    try {
      const response = await fetch(
        `${BASE_URL}/patientRegistrationReferred/draft?ris_patients_id=${appointment.ris_patients_id}`,
        { method: 'DELETE' }
      );
      if (response.ok) setDraft(null);
    } catch (error) {
      logger.error('Error discarding registration draft', error);
    }
  };

  const currentIndex = LOCATION_STAGES.findIndex((s) => s === currentStatus);

  return (
    <div className="ris-location-modal-overlay" onClick={onCancel}>
      <div className="ris-location-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ris-location-modal-header">
          <h3>Patient Location — {appointment?.patient_name || 'N/A'}</h3>
          <button onClick={onCancel} className="ris-location-modal-close">✕</button>
        </div>

        <div className="ris-location-modal-body">
          <div className="ris-location-stepper">
            {LOCATION_STAGES.map((stage, index) => {
              const stageState = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'upcoming';
              const color = getLocationStageColor(stage);
              return (
                <button
                  key={stage}
                  className={`ris-location-stepper-row ris-location-stepper-row--${stageState}`}
                  style={{ '--stage-color': color }}
                  onClick={() => onSelectStatus(stage)}
                >
                  <span className="ris-location-stepper-dot" />
                  <span className="ris-location-stepper-label">{stage}</span>
                  {stageState === 'current' && <span className="ris-location-stepper-current-tag">Current</span>}
                </button>
              );
            })}
          </div>

          {draft && (
            <div className="ris-location-draft-panel">
              <h4>Unfinished Registration</h4>
              <p className="ris-location-draft-note">
                This patient has registration answers that were never submitted,
                last saved {formatSavedAt(draft.age_seconds)}. Resuming reopens the
                form with those answers filled back in. The HIPAA signature is not
                saved and will be collected again.
              </p>
              <div className="ris-location-draft-actions">
                <button
                  className="ris-location-draft-resume"
                  onClick={() => onResumeRegistration?.(appointment)}
                >
                  Resume Registration
                </button>
                <button className="ris-location-draft-discard" onClick={handleDiscardDraft}>
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="ris-location-duration-breakdown">
            <h4>Time in Each Stage</h4>
            {loading ? (
              <p className="ris-location-duration-loading">Loading...</p>
            ) : statusDurations.length === 0 ? (
              <p className="ris-location-duration-loading">No status history yet.</p>
            ) : (
              <ul className="ris-location-duration-list">
                {statusDurations.map((entry, i) => {
                  const isLiveOngoing = entry.ongoing && ongoingBaselineRef.current?.status === entry.status;
                  const displaySeconds = isLiveOngoing
                    ? (nowMs - ongoingBaselineRef.current.baselineMs) / 1000
                    : entry.seconds;
                  return (
                    <li key={i}>
                      <span className="ris-location-duration-status">{entry.status}</span>
                      <span className="ris-location-duration-value">
                        {formatElapsed(displaySeconds)}{entry.ongoing ? ' (ongoing)' : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="ris-location-modal-footer">
          <button onClick={onCancel} className="ris-location-modal-secondary-btn">Close</button>
        </div>
      </div>
    </div>
  );
}

export default LocationModal;
